import React, { useState, useRef } from 'react';
import { Trash2, AlertTriangle, Loader2, CheckCircle, RefreshCw, Package, Upload, Download } from 'lucide-react';
import { adminAPI, stockAPI } from '../../services/api';
import { toast } from 'sonner';

const DataManagementTab = () => {
  const [cleaning, setCleaning] = useState(false);
  const [purging, setPurging] = useState(false);
  const [deletingStock, setDeletingStock] = useState(false);
  const [result, setResult] = useState(null);
  const [purgeResult, setPurgeResult] = useState(null);
  const [stockDeleteResult, setStockDeleteResult] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [stockDeleteConfirm, setStockDeleteConfirm] = useState('');

  // Replace Opening Stock state
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [replaceResult, setReplaceResult] = useState(null);
  const replaceFileRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await stockAPI.downloadOpeningStockTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opening_stock_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleReplaceOpeningStock = async () => {
    if (!replaceFile) return;
    if (!window.confirm('This will DELETE all existing opening stock and replace with the uploaded file. Other transactions (Inwards, Dispatches, Production) will remain intact. Continue?')) {
      return;
    }
    try {
      setReplacing(true);
      setReplaceResult(null);
      const result = await stockAPI.replaceOpeningStock(replaceFile);
      setReplaceResult(result);
      if (result.created > 0 || result.deleted_opening_entries > 0) {
        toast.success(`Opening stock replaced: Deleted ${result.deleted_opening_entries} old entries, Created ${result.created} new entries`);
      } else {
        toast.warning('No changes made — check the details below');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setReplacing(false);
    }
  };

  const handleDeleteAllStock = async () => {
    if (stockDeleteConfirm !== 'DELETE STOCK') {
      toast.error('Type DELETE STOCK to confirm');
      return;
    }
    setDeletingStock(true);
    setStockDeleteResult(null);
    try {
      const res = await stockAPI.deleteAllStockWithAdjustments();
      setStockDeleteResult(res);
      setStockDeleteConfirm('');
      toast.success(res.message || 'All stock deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete stock');
    } finally {
      setDeletingStock(false);
    }
  };

  const handleCleanAll = async () => {
    if (confirmText !== 'DELETE ALL') {
      toast.error('Type DELETE ALL to confirm');
      return;
    }
    setCleaning(true);
    setResult(null);
    try {
      const res = await adminAPI.cleanAllData();
      setResult(res);
      setConfirmText('');
      toast.success(res.message || 'All data cleaned successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clean data');
    } finally {
      setCleaning(false);
    }
  };

  const handlePurgeTransactions = async () => {
    if (purgeConfirmText !== 'PURGE') {
      toast.error('Type PURGE to confirm');
      return;
    }
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await adminAPI.purgeTransactions();
      setPurgeResult(res);
      setPurgeConfirmText('');
      toast.success(res.message || 'Transactions purged successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge transactions');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="data-management-tab">
      {/* Replace Opening Stock (FY Audited) */}
      <div className="bg-white rounded-xl border border-amber-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-50 rounded-lg">
            <Upload className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Replace Opening Stock (FY Audited)</h3>
            <p className="text-[11px] text-gray-500">Replace all opening stock with audited figures — use after financial year closing</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-amber-800">
              <p className="font-semibold mb-1">This will:</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                <li>DELETE all previous opening stock entries</li>
                <li>INSERT fresh opening stock from your Excel</li>
                <li>Only items in Excel will have opening stock</li>
              </ul>
              <p className="font-semibold mt-2 text-green-800">Preserved (untouched):</p>
              <ul className="space-y-0.5 ml-3 list-disc text-green-800">
                <li>All other transactions (Inwards, Dispatches, Production, Adjustments)</li>
                <li>Products, Customers, Vendors, BOMs, Users</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-[10px] text-blue-800 font-semibold mb-1">Expected Excel Format (same as Import):</p>
          <p className="text-[10px] text-blue-700">Col A: SKU | Col B: Name | Col D: Unit | Col I: Selling Price | Col J: Cost Price</p>
          <p className="text-[10px] text-blue-700">Col K-R: Branch quantities (Ranchi, Siliguri, Sasaram, Patna, Bhubaneswar, Bangalore, Lucknow, Bhiwandi)</p>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
            data-testid="download-template-btn"
          >
            <Download className="w-3 h-3" /> Download blank template (.xlsx)
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1 block">
              Upload Audited Opening Stock Excel
            </label>
            <input
              ref={replaceFileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setReplaceFile(e.target.files[0] || null)}
              className="w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 cursor-pointer"
              data-testid="replace-file-input"
            />
            {replaceFile && <p className="text-[10px] text-green-600 mt-1">Selected: {replaceFile.name}</p>}
          </div>

          <button
            onClick={handleReplaceOpeningStock}
            disabled={replacing || !replaceFile}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="replace-opening-stock-btn"
          >
            {replacing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {replacing ? 'Replacing...' : 'Replace Opening Stock'}
          </button>
        </div>

        {replaceResult && (
          <div className={`mt-4 p-3 rounded-lg border ${replaceResult.created > 0 || replaceResult.deleted_opening_entries > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="replace-result">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className={`w-4 h-4 ${replaceResult.created > 0 || replaceResult.deleted_opening_entries > 0 ? 'text-green-600' : 'text-red-600'}`} />
              <span className={`text-xs font-semibold ${replaceResult.created > 0 || replaceResult.deleted_opening_entries > 0 ? 'text-green-800' : 'text-red-800'}`}>{replaceResult.message}</span>
            </div>
            <div className={`text-[10px] space-y-0.5 ${replaceResult.created > 0 || replaceResult.deleted_opening_entries > 0 ? 'text-green-700' : 'text-red-700'}`}>
              <p>Old opening entries deleted: <strong>{replaceResult.deleted_opening_entries}</strong></p>
              <p>New opening entries created: <strong>{replaceResult.created}</strong></p>
              <p>Products matched: <strong>{replaceResult.products_matched}</strong></p>
              <p>Branches: <strong>{(replaceResult.branch_names || []).join(', ') || 'None detected'}</strong></p>
              {replaceResult.products_unmatched > 0 && (
                <div className="mt-1 text-amber-700">
                  <p className="font-semibold">Unmatched products ({replaceResult.products_unmatched}):</p>
                  {(replaceResult.unmatched_products || []).slice(0, 10).map((u, i) => (
                    <p key={i} className="ml-2">Row {u.row}: {u.sku} — {u.name}</p>
                  ))}
                  {replaceResult.products_unmatched > 10 && <p className="ml-2">... and {replaceResult.products_unmatched - 10} more</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Purge Transactions Only */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-50 rounded-lg">
            <RefreshCw className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Purge Transactions</h3>
            <p className="text-[11px] text-gray-500">Delete all transactions but keep master data and opening stock</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-amber-800">
              <p className="font-semibold mb-1">This will permanently delete:</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                <li>Quotations (Signage + Generic), Order Confirmations, Proforma Invoices</li>
                <li>Indents, Purchase Orders, Inward entries</li>
                <li>Production Orders (all statuses)</li>
                <li>Dispatches, Sales Returns, Stock Transfers</li>
                <li>Transaction-generated stock (Production, Sales Return, Branch Transfer)</li>
                <li>Approval requests, Document revisions, Activity logs</li>
              </ul>
              <p className="font-semibold mt-2 text-green-800">Preserved (untouched):</p>
              <ul className="space-y-0.5 ml-3 list-disc text-green-800">
                <li>Products, Customers, Vendors, BOMs</li>
                <li>Opening Stock (your original uploaded inventory)</li>
                <li>Branches, Users, Rate Cards, Settings</li>
                <li>Billing Addresses, Salespersons</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1 block">
              Type "PURGE" to confirm
            </label>
            <input
              type="text"
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder="PURGE"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 font-mono"
              data-testid="purge-confirm-input"
            />
          </div>

          <button
            onClick={handlePurgeTransactions}
            disabled={purging || purgeConfirmText !== 'PURGE'}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="purge-transactions-btn"
          >
            {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {purging ? 'Purging...' : 'Purge Transactions'}
          </button>
        </div>

        {purgeResult && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="purge-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-green-800">{purgeResult.message}</span>
            </div>
            {purgeResult.details && Object.keys(purgeResult.details).length > 0 && (
              <div className="text-[10px] text-green-700 space-y-0.5">
                {Object.entries(purgeResult.details).map(([key, count]) => (
                  <p key={key}>{key}: {count}{typeof count === 'number' ? (key.includes('preserved') ? ' kept' : ' deleted') : ''}</p>
                ))}
              </div>
            )}
            {purgeResult.preserved && (
              <p className="text-[10px] text-green-600 mt-1 font-semibold">Preserved: {purgeResult.preserved.join(', ')}</p>
            )}
          </div>
        )}
      </div>

      {/* Delete All Stock */}
      <div className="bg-white rounded-xl border border-orange-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Package className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Delete All Stock</h3>
            <p className="text-[11px] text-gray-500">Remove all stock items including opening stock — use before re-importing</p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-orange-800">
              <p className="font-semibold mb-1">This will permanently delete:</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                <li>ALL stock items (including Opening stock)</li>
                <li>ALL manual adjustment records (MAJ history)</li>
              </ul>
              <p className="font-semibold mt-2 text-green-800">Preserved (untouched):</p>
              <ul className="space-y-0.5 ml-3 list-disc text-green-800">
                <li>Products, Customers, Vendors, BOMs, Branches, Users</li>
                <li>Transactions (POs, OCs, Quotations, Dispatches, etc.)</li>
              </ul>
              <p className="font-semibold mt-2 text-blue-700">After deleting, re-import opening stock from Stock Inventory &gt; Import Opening Stock</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1 block">
              Type "DELETE STOCK" to confirm
            </label>
            <input type="text" value={stockDeleteConfirm} onChange={(e) => setStockDeleteConfirm(e.target.value)}
              placeholder="DELETE STOCK"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 font-mono"
              data-testid="stock-delete-confirm-input" />
          </div>
          <button onClick={handleDeleteAllStock} disabled={deletingStock || stockDeleteConfirm !== 'DELETE STOCK'}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="delete-all-stock-btn">
            {deletingStock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
            {deletingStock ? 'Deleting...' : 'Delete All Stock'}
          </button>
        </div>

        {stockDeleteResult && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="stock-delete-result">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-green-800">{stockDeleteResult.message}</span>
            </div>
            <p className="text-[10px] text-blue-600 font-semibold mt-1">Now go to Stock Inventory &gt; Import Opening Stock to re-import your stock data</p>
          </div>
        )}
      </div>

      {/* Clean All Data (Nuclear Option) */}
      <div className="bg-white rounded-xl border border-red-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-50 rounded-lg">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Clean All Data</h3>
            <p className="text-[11px] text-gray-500">Remove all transactional AND master data for a completely fresh start</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-red-800">
              <p className="font-semibold mb-1">This will permanently delete EVERYTHING:</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                <li>Products, Stock Items, Inwards</li>
                <li>Order Confirmations, Purchase Orders, Dispatches</li>
                <li>Quotations, Proforma Invoices, Sales Returns</li>
                <li>Customers, Vendors, BOMs, Payments</li>
                <li>Site Projects, Indents, All activity logs</li>
              </ul>
              <p className="font-semibold mt-2">Preserved: Users, Branches, Role Permissions, System Settings only</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1 block">
              Type "DELETE ALL" to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE ALL"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200 font-mono"
              data-testid="clean-confirm-input"
            />
          </div>

          <button
            onClick={handleCleanAll}
            disabled={cleaning || confirmText !== 'DELETE ALL'}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="clean-all-data-btn"
          >
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {cleaning ? 'Cleaning...' : 'Clean All Data'}
          </button>
        </div>

        {result && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="clean-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-green-800">{result.message}</span>
            </div>
            {result.details && Object.keys(result.details).length > 0 && (
              <div className="text-[10px] text-green-700 space-y-0.5">
                {Object.entries(result.details).map(([coll, count]) => (
                  <p key={coll}>{coll}: {count} deleted</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataManagementTab;
